import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePermissions } from "../../../contexts/PermissionsContext";
import {
  Loader2,
  X,
  MessageSquare,
  CheckCheck,
  AlertCircle,
  Send,
  Edit3,
  Copy,
  Package,
  Info,
  User,
  UserCheck,
  CheckCircle2,
  Clock,
  Lock,
} from "lucide-react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ar";

import {
  fetchProductById,
  clearCurrentProduct,
} from "../../../RTK/orders/GetAllOrders";
import ProductPopUp from "../../product/product-popup/ProductPopUp";
import { fetchAgentDetails } from "../../../RTK/agents/GetAgents";
import AgentDetailsModal from "../../agent/agent-popup/AgentDetailsModal ";

import UserDetailsPopup from "../../users/UserDetailsPopup";
import { fetchUserDetails } from "../../../RTK/users/GetAllUsers";

const formatTimeAgo = (dateString) => {
  if (!dateString) return "N/A";
  
  const now = dayjs();
  const date = dayjs(dateString);
  const diffInSeconds = now.diff(date, 'second');
  const diffInMinutes = now.diff(date, 'minute');
  const diffInHours = now.diff(date, 'hour');
  const diffInDays = now.diff(date, 'day');
  const diffInMonths = now.diff(date, 'month');
  const diffInYears = now.diff(date, 'year');

  if (diffInSeconds < 60) {
    return `منذ ${diffInSeconds} ثانية`;
  } else if (diffInMinutes < 60) {
    return `منذ ${diffInMinutes} دقيقة`;
  } else if (diffInHours < 24) {
    return `منذ ${diffInHours} ساعة`;
  } else if (diffInDays < 30) {
    return `منذ ${diffInDays} يوم`;
  } else if (diffInMonths < 12) {
    return `منذ ${diffInMonths} شهر`;
  } else {
    return `منذ ${diffInYears} سنة`;
  }
};

dayjs.extend(relativeTime);
dayjs.locale("ar");

const OrderDetailsPopup = ({
  order,
  onClose,
  StatusBadge,
  onUpdateStatus,
  canUpdateStatus,
}) => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions(); // ✅ إضافة

  // ✅ فحص جميع الصلاحية
  const canRetryPendingOrders = hasPermission("retry_pending_orders");
  const canViewAgents = hasPermission("view_agents");
  const canViewProducts = hasPermission("view_products");
  const canViewUserDetails = hasPermission("view_user_details");

  const { currentProduct } = useSelector((state) => state.orders);
  const { agentDetails } = useSelector((state) => state.allagents);
  const orderDetails = order?.[0]?.order_data || {};
  const order_id = order?.[0]?.order_id;
  const userData = order?.[0]?.user_data || {};
  const userIdRef = useRef(null);
  if (userIdRef.current === null && userData.id) {
    userIdRef.current = userData.id;
  }

  const [selectedStatus, setSelectedStatus] = useState(orderDetails?.status);
  const [replayMessage, setReplayMessage] = useState(orderDetails?.reply || "");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [showProductPopup, setShowProductPopup] = useState(false);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);

  // ✅ حالات الوكيل
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [isLoadingAgent, setIsLoadingAgent] = useState(false);

  // ✅ حالات المستخدم
  const [showUserPopup, setShowUserPopup] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);

  useEffect(() => {
    setSelectedStatus(orderDetails?.status);
    setReplayMessage(orderDetails?.reply || "");
  }, [orderDetails]);

  // ✅ دالة فتح Product Popup
  const handleProductClick = async () => {
    if (!canViewProducts) {
      toast.error("ليس لديك صلاحية لعرض تفاصيل المنتجات 🔒");
      return;
    }

    if (!orderDetails.product) {
      toast.error("معرف المنتج غير متوفر");
      return;
    }

    setIsLoadingProduct(true);
    try {
      await dispatch(fetchProductById(orderDetails.product)).unwrap();
      setShowProductPopup(true);
    } catch (error) {
      console.error("Error loading product:", error);
      toast.error("فشل في تحميل تفاصيل المنتج");
    } finally {
      setIsLoadingProduct(false);
    }
  };

  // ✅ دالة إغلاق Product Popup
  const handleCloseProductPopup = () => {
    setShowProductPopup(false);
    dispatch(clearCurrentProduct());
  };

  // ✅ دالة فتح Agent Modal
  const handleAgentClick = async () => {
    if (!canViewAgents) {
      toast.error("ليس لديك صلاحية لعرض تفاصيل الوكلاء 🔒");
      return;
    }

    if (!orderDetails.cheapest_agent_id) {
      toast.error("معرف الوكيل غير متوفر");
      return;
    }

    setIsLoadingAgent(true);
    try {
      await dispatch(
        fetchAgentDetails(orderDetails.cheapest_agent_id),
      ).unwrap();
      setShowAgentModal(true);
    } catch (error) {
      console.error("Error loading agent:", error);
      toast.error("فشل في تحميل تفاصيل الوكيل");
    } finally {
      setIsLoadingAgent(false);
    }
  };

  // ✅ دالة إغلاق Agent Modal
  const handleCloseAgentModal = () => {
    setShowAgentModal(false);
  };

  // ✅ دالة فتح User Popup
  const handleUserClick = async () => {
    if (!canViewUserDetails) {
      toast.error("ليس لديك صلاحية لعرض تفاصيل المستخدمين 🔒");
      return;
    }

    if (!userData.id) {
      toast.error("معرف المستخدم غير متوفر");
      return;
    }

    setIsLoadingUser(true);
    try {
      await dispatch(fetchUserDetails(userData.id)).unwrap();
      setShowUserPopup(true);
    } catch (error) {
      console.error("Error loading user:", error);
      toast.error("فشل في تحميل تفاصيل المستخدم");
    } finally {
      setIsLoadingUser(false);
    }
  };

  // ✅ دالة إغلاق User Popup
  const handleCloseUserPopup = () => {
    setShowUserPopup(false);
  };

  const statusOptions = ["pending", "completed", "cancelled"];

  const replaySuggestions = {
    completed: [
      "تم الشحن بنجاح ✅",
      "تم إضافة الرصيد بنجاح",
      "تمت العملية بنجاح في الوقت المحدد",
      "شكراً لك، تم تنفيذ طلبك بنجاح",
      "تم التفعيل بنجاح",
      "العملية مكتملة بنجاح",
    ],
    cancelled: [
      "معرف الحساب غير صحيح ❌",
      "تأخير من طرف المستخدم",
      "الحساب محظور أو غير متاح",
      "تواصل مع الدعم الفني",
      "خطأ في البيانات المدخلة",
      "الخدمة غير متوفرة حالياً",
      "تم إلغاء الطلب بناءً على طلبك",
      "مشكلة فنية، يرجى المحاولة لاحقاً",
    ],
    pending: [
      "جاري المعالجة... ⏳",
      "في انتظار الرد من المزود",
      "طلبك قيد التنفيذ",
      "قد يستغرق الأمر بعض الوقت",
      "جاري التحقق من البيانات",
    ],
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;

    // ✅ فحص الصلاحية للطلبات المعلقة
    if (orderDetails?.status === "pending" && !canRetryPendingOrders) {
      toast.error("ليس لديك صلاحية لتعديل الطلبات المعلقة 🔒");
      return;
    }

    setSelectedStatus(newStatus);

    if (newStatus !== orderDetails?.status) {
      setShowSuggestions(true);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    setReplayMessage(suggestion);
    setShowSuggestions(false);
  };

  const handleStatusUpdate = async () => {
    // ✅ فحص الصلاحية قبل التحديث
    if (orderDetails?.status === "pending" && !canRetryPendingOrders) {
      toast.error("ليس لديك صلاحية لإعادة توجيه الطلبات المعلقة 🔒");
      return;
    }

    if (
      selectedStatus !== orderDetails?.status ||
      replayMessage !== orderDetails?.reply
    ) {
      setIsLoading(true);
      try {
        await onUpdateStatus(order_id, selectedStatus, replayMessage.trim());
        toast.success(`تم تحديث الحالة والرد بنجاح`);
        onClose();
      } catch (error) {
        console.error("Update error:", error);
        toast.error("فشل في تحديث الحالة. حاول مرة أخرى.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  // ✅ دالة نسخ التفاصيل (بدون: المستخدم، الوكيل، معرف المعاملة للوكيل، )
  const handleCopyOrderDetails = () => {
    const detailsText = `
تفاصيل الطلب
━━━━━━━━━━━━━━━━━━━━

رقم الطلب: ${orderDetails.transaction_id || "غير متوفر"}

المنتج: ${orderDetails.product_name || "غير متوفر"}
الكمية: ${orderDetails.quantity || "0"}
النوع: ${orderDetails.product_type || "غير متوفر"}

التاريخ: ${orderDateFormatted.date}
الوقت: ${orderDateFormatted.time}
وقت التنفيذ: ${formatExecutionTime(orderDetails.execution_time)}

الحالة: ${selectedStatus === "completed" ? "مكتمل" : selectedStatus === "cancelled" ? "ملغي" : "معلق"}

الرد: ${replayStatus.text}

متطلبات المنتج:
${
  orderDetails.product_type === "stock"
    ? orderDetails.product_requirements_data
    : JSON.stringify(
        JSON.parse(orderDetails.product_requirements_data || "{}"),
        null,
        2,
      )
}

━━━━━━━━━━━━━━━━━━━━
  `.trim();

    navigator.clipboard
      .writeText(detailsText)
      .then(() => {
        // ✅ غير الأيقونة لـ CheckCheck
        setIsCopied(true);

        // ✅ ارجعها لـ Copy بعد 2 ثانية
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      })
      .catch((err) => {
        console.error("Copy failed:", err);
        alert("❌ فشل النسخ، حاول مرة أخرى");
      });
  };

  // ✅ دالة نسخ حقل معين
  const handleCopyField = (value, fieldName) => {
    if (!value) {
      toast.error("لا توجد قيمة للنسخ");
      return;
    }

    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedField(fieldName);
        toast.success(`تم نسخ ${fieldName} بنجاح `);

        setTimeout(() => {
          setCopiedField(null);
        }, 2000);
      })
      .catch((err) => {
        console.error("Copy failed:", err);
        toast.error("فشل في النسخ");
      });
  };

  const [copiedField, setCopiedField] = useState(null);

  const getReplayStatus = (replay, orderStatus) => {
    if (!replay || replay === null || replay === "-") {
      return {
        icon: MessageSquare,
        color: "text-gray-400",
        bgColor: "bg-gray-500/10",
        borderColor: "border-gray-500/20",
        text: "لا يوجد رد",
      };
    }

    const successKeywords = [
      "successful",
      "تم",
      "success",
      "completed",
      "نجح",
      "بنجاح",
    ];
    const errorKeywords = ["failed", "error", "فشل", "خطأ", "cancelled"];

    const replayLower = replay.toLowerCase();
    const isSuccess = successKeywords.some((keyword) =>
      replayLower.includes(keyword.toLowerCase()),
    );
    const isError = errorKeywords.some((keyword) =>
      replayLower.includes(keyword.toLowerCase()),
    );

    if (isSuccess || orderStatus === "completed") {
      return {
        icon: CheckCheck,
        color: "text-green-400",
        bgColor: "bg-green-500/10",
        borderColor: "border-green-500/20",
        text: replay,
      };
    } else if (isError || orderStatus === "cancelled") {
      return {
        icon: AlertCircle,
        color: "text-red-400",
        bgColor: "bg-red-500/10",
        borderColor: "border-red-500/20",
        text: replay,
      };
    } else {
      return {
        icon: MessageSquare,
        color: "text-blue-400",
        bgColor: "bg-blue-500/10",
        borderColor: "border-blue-500/20",
        text: replay,
      };
    }
  };

  const toArabicNumbers = (num) => {
    return num.toString().replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[d]);
  };

  const formatExecutionTime = (microseconds) => {
    if (!microseconds || microseconds === "0") return "٠ ثانية";

    const totalSeconds = Math.floor(parseInt(microseconds) / 1000000);
    const convert = toArabicNumbers;

    if (totalSeconds < 60) {
      return `${convert(totalSeconds)} ثانية`;
    } else if (totalSeconds < 3600) {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return seconds > 0
        ? `${convert(minutes)} دقيقة ${convert(seconds)} ثانية`
        : `${convert(minutes)} دقيقة`;
    } else {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      let result = `${convert(hours)} ساعة`;
      if (minutes > 0) result += ` ${convert(minutes)} دقيقة`;
      if (seconds > 0) result += ` ${convert(seconds)} ثانية`;

      return result;
    }
  };

  const formatOrderDate = (dateString) => {
    if (!dateString) return { date: "غير متوفر", time: "" };

    const date = dayjs(dateString);
    const formattedDate = date.format("DD/MM/YYYY");
    const formattedTime = date.format("h:mm:ss A");

    return { date: formattedDate, time: formattedTime };
  };

  const replayStatus = getReplayStatus(orderDetails.reply, orderDetails.status);
  const hasChanges =
    selectedStatus !== orderDetails?.status ||
    replayMessage !== orderDetails?.reply;
  const orderDateFormatted = formatOrderDate(orderDetails.execution_start_time); // ✅ تغيير من order_date

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2000] p-4 ${
          showAgentModal || showProductPopup ? "pointer-events-none" : ""
        }`}
        onClick={!showAgentModal && !showProductPopup ? handleClose : undefined}
      >
        <div
          className={`${
            isClosing ? "animate-close" : "animate-popup"
          } bg-slate-900 rounded-2xl w-full max-w-2xl border border-slate-800 shadow-xl animate-in zoom-in-95 z-[2100] max-h-[90vh] overflow-y-auto pointer-events-auto`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-bold text-white">تفاصيل الطلب</h3>

                <button
                  onClick={handleCopyOrderDetails}
                  className={`p-2 rounded-lg transition-all group ${
                    isCopied
                      ? "bg-green-500/20 hover:bg-green-500/30"
                      : "hover:bg-blue-500/20"
                  }`}
                  title={isCopied ? "تم النسخ!" : "نسخ تفاصيل الطلب"}
                >
                  {isCopied ? (
                    <CheckCheck className="w-4 h-4 text-green-400 transition-all" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-400 group-hover:text-blue-400 transition-colors" />
                  )}
                </button>
              </div>

              {/* ✅ Transaction ID مع خاصية النسخ */}
              <button
                onClick={() =>
                  handleCopyField(orderDetails.transaction_id, "رقم الطلب")
                }
                className="group flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-800 transition-all"
                title="انقر للنسخ"
              >
                <span className="text-sm font-serif text-gray-500 group-hover:text-gray-300 transition-colors">
                  #{orderDetails.transaction_id}
                </span>
                {copiedField === "رقم الطلب" ? (
                  <CheckCheck className="w-3.5 h-3.5 text-green-400" />
                ) : (
                  <Copy className="w-3.5 h-3.5 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </button>

              <button
                onClick={handleClose}
                className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400 hover:text-red-400 transition-colors" />
              </button>
            </div>

            {/* ✅ رسالة تحذيرية للطلبات المعلقة */}
            {orderDetails?.status === "pending" && !canRetryPendingOrders && (
              <div className="flex items-center gap-2 p-3 bg-orange-900/20 border border-orange-500/30 rounded-lg">
                <Lock className="w-5 h-5 text-orange-400" />
                <span className="text-orange-300 text-sm">
                  ليس لديك صلاحية لإعادة توجيه الطلبات المعلقة
                </span>
              </div>
            )}

            {/* Status and Update Section */}
            <div className="bg-slate-800/30 rounded-xl p-4 space-y-4">
              {/* ✅ Container رئيسي */}
              <div className="flex flex-col gap-3">
                {/* ✅ الصف الأول: Status Badge + Processing + Approval */}
                <div className="flex items-center justify-between gap-2">
                  {/* Status Badge */}
                  <div className="flex-shrink-0">
                    <StatusBadge status={selectedStatus} />
                  </div>

                  {/* Processing By */}
                  {orderDetails.processed_by && (
                    <div
                      className="group relative flex-shrink-0"
                      title={`تمت المعالجة بواسطة: ${orderDetails.processed_by}`}
                    >
                      <div className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-2xl transition-all cursor-help bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20">
                        <UserCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-400 flex-shrink-0" />
                        <span className="text-[10px] sm:text-xs font-medium text-blue-300 whitespace-nowrap">
                          {orderDetails.processed_by}
                        </span>
                      </div>

                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50 shadow-lg">
                        معالجة: {orderDetails.processed_by}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  )}

                  {/* Approved/Cancelled By */}
                  {/* {orderDetails.approved_by && (
                    <div
                      className="group relative flex-shrink-0"
                      title={
                        orderDetails.status === "cancelled"
                          ? `تم الإلغاء بواسطة: ${orderDetails.approved_by}`
                          : orderDetails.status === "pending"
                            ? `قيد المعالجة بواسطة: ${orderDetails.approved_by}`
                            : `تمت الموافقة بواسطة: ${orderDetails.approved_by}`
                      }
                    >
                      <div
                        className={`flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-2xl transition-all cursor-help ${
                          orderDetails.status === "cancelled"
                            ? "bg-red-500/10 border border-red-500/20 hover:bg-red-500/20"
                            : orderDetails.status === "pending"
                              ? "bg-yellow-500/10 border border-yellow-500/20 hover:bg-yellow-500/20"
                              : "bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20"
                        }`}
                      >
                        {orderDetails.status === "cancelled" ? (
                          <AlertCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-red-400 flex-shrink-0" />
                        ) : orderDetails.status === "pending" ? (
                          <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-yellow-400 flex-shrink-0" />
                        ) : (
                          <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-400 flex-shrink-0" />
                        )}
                        <span
                          className={`text-[10px] sm:text-xs font-medium whitespace-nowrap ${
                            orderDetails.status === "cancelled"
                              ? "text-red-300"
                              : orderDetails.status === "pending"
                                ? "text-yellow-300"
                                : "text-emerald-300"
                          }`}
                        >
                          {orderDetails.approved_by}
                        </span>
                      </div>

                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50 shadow-lg">
                        {orderDetails.status === "cancelled"
                          ? "إلغاء"
                          : orderDetails.status === "pending"
                            ? "معلق"
                            : "موافقة"}
                        : {orderDetails.approved_by}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  )} */}
                </div>

                {/* ✅ الصف الثاني: Execution Time + Dropdown */}
                <div className="flex items-center justify-between gap-2">
                  {/* Execution Time */}
                  <div className="bg-gray-800 rounded-2xl px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs text-white flex items-center gap-1 sm:gap-2 flex-1">
                    <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                    <span className="whitespace-nowrap">
                      {formatExecutionTime(orderDetails.execution_time)}
                    </span>
                  </div>

                  {/* Status Selector */}
                  <div className="relative flex-shrink-0">
                    <select
                      id="status"
                      value={selectedStatus}
                      onChange={handleStatusChange}
                      disabled={
                        orderDetails?.status === "pending" &&
                        !canRetryPendingOrders
                      }
                      className={`bg-slate-700 text-white text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 border border-slate-600 appearance-none pr-6 sm:pr-8 ${
                        orderDetails?.status === "pending" &&
                        !canRetryPendingOrders
                          ? "cursor-not-allowed opacity-50"
                          : "cursor-pointer"
                      }`}
                    >
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status === "completed"
                            ? "مكتمل"
                            : status === "cancelled"
                              ? "ملغي"
                              : "معلق"}
                        </option>
                      ))}
                    </select>
                    <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none">
                      {orderDetails?.status === "pending" &&
                      !canRetryPendingOrders ? (
                        <Lock className="w-3 h-3 text-slate-500" />
                      ) : (
                        <svg
                          className="w-3 h-3 text-slate-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Replay Section */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm text-slate-300 font-medium">
                  <MessageSquare className="w-4 h-4" />
                  رد الوكيل
                </label>

                <div className="relative">
                  <textarea
                    value={replayMessage}
                    onChange={(e) => setReplayMessage(e.target.value)}
                    disabled={
                      orderDetails?.status === "pending" &&
                      !canRetryPendingOrders
                    }
                    placeholder="اكتب رد الوكيل هنا..."
                    className={`w-full h-[60px] bg-slate-700 text-white p-3 rounded-lg border border-slate-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors resize-none text-sm ${
                      orderDetails?.status === "pending" &&
                      !canRetryPendingOrders
                        ? "cursor-not-allowed opacity-50"
                        : ""
                    }`}
                    rows="3"
                    dir="rtl"
                  />

                  <button
                    type="button"
                    onClick={() => setShowSuggestions(!showSuggestions)}
                    disabled={
                      orderDetails?.status === "pending" &&
                      !canRetryPendingOrders
                    }
                    className={`absolute left-2 top-2 p-1 text-slate-400 hover:text-white transition-colors ${
                      orderDetails?.status === "pending" &&
                      !canRetryPendingOrders
                        ? "cursor-not-allowed opacity-50"
                        : ""
                    }`}
                    title="اقتراحات الردود"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                </div>

                {/* اقتراحات الردود */}
                {showSuggestions &&
                  replaySuggestions[selectedStatus] &&
                  (orderDetails?.status !== "pending" ||
                    canRetryPendingOrders) && (
                    <div className="bg-slate-800 rounded-lg border border-slate-600 p-3">
                      <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        اقتراحات للحالة:{" "}
                        {selectedStatus === "completed"
                          ? "مكتمل"
                          : selectedStatus === "cancelled"
                            ? "ملغي"
                            : "معلق"}
                      </h4>
                      <div className="grid grid-cols-1 gap-2">
                        {replaySuggestions[selectedStatus].map(
                          (suggestion, index) => (
                            <button
                              key={index}
                              onClick={() => handleSuggestionSelect(suggestion)}
                              className="text-right p-2 bg-slate-700/50 hover:bg-slate-700 rounded-lg text-sm text-slate-300 hover:text-white transition-colors border border-slate-600/50 hover:border-slate-500"
                            >
                              {suggestion}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        onClick={() => setShowSuggestions(false)}
                        className="mt-2 text-xs text-slate-400 hover:text-slate-300 transition-colors"
                      >
                        إغلاق الاقتراحات
                      </button>
                    </div>
                  )}

                {/* عرض الرد الحالي - محدود داخل السكشن */}
                {orderDetails.reply && (
                  <div
                    className={`bg-slate-800/50 rounded-xl py-3 px-4 border ${replayStatus.borderColor} ${replayStatus.bgColor}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="text-sm text-slate-400">
                          الرد الحالي:
                        </div>
                        <replayStatus.icon
                          className={`w-4 h-4 ${replayStatus.color} flex-shrink-0`}
                        />
                      </div>
                      <div
                        className={`text-sm ${replayStatus.color} font-medium break-words text-right flex-1 overflow-hidden`}
                      >
                        {replayStatus.text}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* باقي تفاصيل الطلب */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-slate-800/50 rounded-xl py-2 px-4 space-y-1 flex items-center justify-between">
                  <div className="w-[60px] h-[50px] relative group">
                    <img
                      src={orderDetails.product_image}
                      alt={orderDetails.product_name}
                      className="w-full h-full object-cover rounded-lg"
                    />

                    {/* ✅ أيقونة Info أو Lock حسب الصلاحية */}
                    <button
                      onClick={handleProductClick}
                      disabled={isLoadingProduct || !canViewProducts}
                      className={`absolute -top-2 -right-2 p-1.5 rounded-full transition-all shadow-lg z-10 ${
                        !canViewProducts
                          ? "bg-slate-700 cursor-not-allowed"
                          : "group-hover:scale-110 bg-purple-500/90 hover:bg-purple-600"
                      }`}
                      title={
                        !canViewProducts
                          ? "ليس لديك صلاحية لعرض تفاصيل المنتجات"
                          : "عرض تفاصيل المنتج"
                      }
                    >
                      {isLoadingProduct ? (
                        <Loader2 className="w-3 h-3 text-white animate-spin" />
                      ) : !canViewProducts ? (
                        <Lock className="w-3 h-3 text-slate-400" />
                      ) : (
                        <Info className="w-3 h-3 text-white" />
                      )}
                    </button>

                    {/* السعر الأساسي */}
                    <div className="absolute bottom-0 left-1 bg-black/80 text-white text-xs text-center px-1 py-0.5 rounded-lg">
                      $
                      {(
                        orderDetails.total_price - orderDetails.profit_margin
                      ).toFixed(4)}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-slate-400">المنتج</div>
                    <div className="mt-2">
                      {orderDetails.product_name || "N/A"}
                    </div>
                  </div>

                  <div className="text-white font-medium mt-1">
                    <div className="text-sm text-slate-400">الكمية</div>
                    {orderDetails.quantity > 0 && (
                      <div className="text-sm text-white mt-2 text-left">
                        {orderDetails.quantity}
                      </div>
                    )}
                  </div>

                  <div className="text-white font-medium mt-1">
                    <div className="text-sm text-slate-400 text-right">
                      النوع
                    </div>
                    <div className="text-sm text-white mt-2 text-right">
                      {orderDetails.product_type}
                    </div>
                  </div>
                </div>

                <div className="w-full bg-slate-800/50 rounded-xl py-2 px-4 space-y-3">
                  <div className="text-sm text-slate-400 text-center">
                    متطلبات المنتج
                  </div>

                  {(() => {
                    // ✅ تنظيف القيمة الأساسية
                    let rawValue = orderDetails.product_type === "stock"
                      ? orderDetails.product_requirements_data
                      : JSON.stringify(
                          JSON.parse(
                            orderDetails.product_requirements_data || "{}",
                          ),
                          null,
                          2,
                        );

                    if (typeof rawValue === 'string') {
                      rawValue = rawValue
                        .replace(/^["']|["']$/g, '')
                        .replace(/\\"/g, '"')
                        .replace(/\\u2014/g, '—');
                    }

                    // ✅ فصل القيم بناءً على الفواصل المختلفة
                    const separators = ['—', '-', ' - ', '\\u2014'];
                    let values = [rawValue];

                    separators.forEach(sep => {
                      const newValues = [];
                      values.forEach(val => {
                        if (val.includes(sep)) {
                          newValues.push(...val.split(sep).map(v => v.trim()));
                        } else {
                          newValues.push(val);
                        }
                      });
                      values = newValues;
                    });

                    // ✅ تنظيف القيم الفارغة
                    values = values.filter(v => v && v.length > 0);

                    // ✅ لو قيمة واحدة
                    if (values.length === 1) {
                      return (
                        <button
                          onClick={() => handleCopyField(values[0], "متطلبات المنتج")}
                          className="w-full group hover:bg-slate-700 rounded-lg transition-all p-2"
                          title="انقر للنسخ"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-purple-400 group-hover:text-purple-300 font-medium transition-colors break-all flex-1 text-center">
                              {values[0]}
                            </span>
                            {copiedField === "متطلبات المنتج" ? (
                              <CheckCheck className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                            ) : (
                              <Copy className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                            )}
                          </div>
                        </button>
                      );
                    }

                    // ✅ لو أكتر من قيمة
                    return (
                      <div className="space-y-2">
                        {values.map((value, index) => (
                          <button
                            key={index}
                            onClick={() => handleCopyField(value, `القيمة ${index + 1}`)}
                            className="w-full group hover:bg-slate-700 rounded-lg transition-all p-2 border border-slate-700"
                            title="انقر للنسخ"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 flex-1">
                                <span className="text-xs text-slate-500">
                                  {index + 1}
                                </span>
                                <span className="text-purple-400 group-hover:text-purple-300 font-medium transition-colors break-all text-left flex-1">
                                  {value}
                                </span>
                              </div>
                              {copiedField === `القيمة ${index + 1}` ? (
                                <CheckCheck className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                              ) : (
                                <Copy className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* ✅ السعر والربح */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-slate-800/50 rounded-xl py-2 px-4 space-y-1 flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-400">السعر</div>
                      <div className="text-emerald-400 font-medium mt-2">
                        ${orderDetails.total_price}
                      </div>
                    </div>

                    <div>
                      <div className="text-sm text-slate-400 text-right">
                        الربح
                      </div>
                      <div className="text-purple-400 font-medium mt-2">
                        ${orderDetails.profit_margin}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* ✅ المستخدم - خط أصغر على الموبايل */}
                  <div className="bg-slate-800/50 rounded-xl py-2 px-4 space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-400">المستخدم</div>
                      <button
                        onClick={handleUserClick}
                        disabled={isLoadingUser || !canViewUserDetails}
                        className={`p-1 rounded-full transition-all shadow-md flex-shrink-0 ${
                          !canViewUserDetails
                            ? "bg-slate-700 cursor-not-allowed"
                            : "hover:scale-110 bg-sky-500/90 hover:bg-sky-600"
                        }`}
                        title={
                          !canViewUserDetails
                            ? "ليس لديك صلاحية لعرض تفاصيل المستخدمين"
                            : "عرض تفاصيل المستخدم"
                        }
                      >
                        {isLoadingUser ? (
                          <Loader2 className="w-3 h-3 text-white animate-spin" />
                        ) : !canViewUserDetails ? (
                          <Lock className="w-3 h-3 text-slate-400" />
                        ) : (
                          <User className="w-3 h-3 text-white" />
                        )}
                      </button>
                    </div>

                    <button
                      onClick={() =>
                        handleCopyField(userData.username, "اسم المستخدم")
                      }
                      className="group flex items-center gap-2 text-white font-medium hover:bg-slate-700 px-2 py-1 rounded-lg transition-all w-full mt-2"
                      title="انقر للنسخ"
                    >
                      <span className="group-hover:text-sky-400 transition-colors break-all flex-1 text-left text-xs sm:text-sm">
                        @{userData.username}
                      </span>
                      {copiedField === "اسم المستخدم" ? (
                        <CheckCheck className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      )}
                    </button>
                  </div>

                  {/* التاريخ */}
                  <div className="bg-slate-800/50 rounded-xl py-2 px-4 space-y-1">
                    <div className="text-sm text-slate-400 text-right flex items-center justify-between">
                      <span className="text-gray-500 text-xs sm:text-sm">
                        {formatTimeAgo(orderDetails.execution_start_time)} {/* ✅ تغيير */}
                      </span>
                      <span>التاريخ</span>
                    </div>
                    <div className="text-slate-300 text-sm text-right flex items-center justify-between">
                      <span className="text-xs sm:text-sm">
                        {orderDateFormatted.date}
                      </span>
                      <span className="text-gray-400 text-xs sm:text-sm">
                        {orderDateFormatted.time}
                      </span>
                    </div>
                  </div>

                </div>

                {/* ✅ قسم الوكيل - تعديل معرف المعاملة */}
                <div className="bg-slate-800/50 rounded-xl py-2 px-4 space-y-3">
                  {/* اسم الوكيل */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-400">الوكيل</div>
                      <div className="text-white font-medium mt-2">
                        {orderDetails.cheapest_agent_name}
                      </div>
                    </div>

                    <button
                      onClick={handleAgentClick}
                      disabled={
                        isLoadingAgent ||
                        !canViewAgents ||
                        orderDetails.cheapest_agent_name === "Daily Card"
                      }
                      className={`p-1 rounded-full transition-all shadow-md flex-shrink-0 ${
                        !canViewAgents ||
                        orderDetails.cheapest_agent_name === "Daily Card"
                          ? "bg-slate-700 cursor-not-allowed"
                          : "hover:scale-110 bg-blue-500/90 hover:bg-blue-600"
                      }`}
                      title={
                        !canViewAgents
                          ? "ليس لديك صلاحية لعرض تفاصيل الوكلاء"
                          : orderDetails.cheapest_agent_name === "Daily Card"
                            ? "الوكيل الافتراضي - لا توجد تفاصيل"
                            : "عرض تفاصيل الوكيل"
                      }
                    >
                      {isLoadingAgent ? (
                        <Loader2 className="w-3 h-3 text-white animate-spin" />
                      ) : !canViewAgents ||
                        orderDetails.cheapest_agent_name === "Daily Card" ? (
                        <Lock className="w-3 h-3 text-slate-400" />
                      ) : (
                        <User className="w-3 h-3 text-white" />
                      )}
                    </button>
                  </div>

                  {/* ✅ معرف المعاملة للوكيل - نسخ بالضغط على القيمة */}
                  <button
                    onClick={() =>
                      handleCopyField(
                        orderDetails.order_agent_id,
                        "معرف المعاملة للوكيل",
                      )
                    }
                    disabled={
                      !orderDetails.order_agent_id ||
                      orderDetails.order_agent_id === "N/A"
                    }
                    className="w-full text-right group hover:bg-slate-700 rounded-lg transition-all p-2"
                    title={
                      orderDetails.order_agent_id &&
                      orderDetails.order_agent_id !== "N/A"
                        ? "انقر للنسخ"
                        : undefined
                    }
                  >
                    <div className="text-sm text-slate-400 mb-1">
                      معرف المعاملة للوكيل
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white font-mono text-sm group-hover:text-sky-400 transition-colors">
                        {orderDetails.order_agent_id || "N/A"}
                      </span>
                      {copiedField === "معرف المعاملة للوكيل" && (
                        <CheckCheck className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                </div>

                {/* ✅ Client Order ID - نسخ بالضغط على القيمة */}
                {orderDetails.client_order_id ? (
                  <button
                    onClick={() =>
                      handleCopyField(
                        orderDetails.client_order_id,
                        "معرف طلب العميل",
                      )
                    }
                    className="w-full bg-slate-800/50 rounded-xl py-2 px-4 text-right group hover:bg-slate-700 transition-all"
                    title="انقر للنسخ"
                  >
                    <div className="text-sm text-slate-400 mb-1">
                      معرف طلب العميل
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white font-mono text-sm break-all group-hover:text-sky-400 transition-colors">
                        {orderDetails.client_order_id}
                      </span>
                      {copiedField === "معرف طلب العميل" && (
                        <CheckCheck className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ) : (
                  <div className="bg-slate-800/50 rounded-xl py-2 px-4 border border-slate-700">
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>لا يوجد معرف طلب عميل لهذا الطلب</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Update Status and Replay Button */}
            <div className="flex justify-end">
              {/* ✅ حماية زر التحديث */}
              {canUpdateStatus !== false && (
                <>
                  {orderDetails?.status === "pending" &&
                  !canRetryPendingOrders ? (
                    <div className="w-full px-6 py-3 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center gap-2 text-slate-400">
                      <Lock className="w-5 h-5" />
                      <span className="text-sm">
                        ليس لديك صلاحية لإعادة توجيه الطلبات المعلقة
                      </span>
                    </div>
                  ) : (
                    <button
                      onClick={handleStatusUpdate}
                      disabled={isLoading || !hasChanges}
                      className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                        hasChanges
                          ? isLoading
                            ? "bg-emerald-600 cursor-not-allowed opacity-70"
                            : "bg-emerald-700 hover:bg-emerald-600 shadow-lg hover:shadow-emerald-500/20"
                          : "bg-slate-700 cursor-not-allowed opacity-50"
                      } text-white`}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          جاري التحديث...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          تحديث الحالة والرد
                        </>
                      )}
                    </button>
                  )}
                </>
              )}

              {canUpdateStatus === false && (
                <div className="w-full px-6 py-3 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center gap-2 text-slate-400">
                  <Lock className="w-5 h-5" />
                  <span className="text-sm">
                    ليس لديك صلاحية لتحديث الطلبات
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Popups - تظهر فقط لو عنده الصلاحية */}
      {showProductPopup && currentProduct && canViewProducts && (
        <ProductPopUp
          title="Product"
          showPopup={showProductPopup}
          setShowPopup={handleCloseProductPopup}
          product={currentProduct}
        />
      )}

      {showAgentModal && agentDetails && canViewAgents && (
        <AgentDetailsModal
          agentDetails={agentDetails}
          onClose={handleCloseAgentModal}
        />
      )}

      {showUserPopup && userIdRef.current && canViewUserDetails && (
        <UserDetailsPopup
          userId={userIdRef.current}
          onClose={handleCloseUserPopup}
        />
      )}
    </>
  );
};

export default OrderDetailsPopup;
